import React, { useEffect, useState, useRef, useCallback } from 'react';
import API_URL from './api'; 
import { ParallaxProvider, Parallax } from 'react-scroll-parallax';
import { Link } from 'react-router-dom';

import VideoModal from './VideoModal'; // Import the extracted VideoModal component

const fontStyle = `
  @font-face {
    font-family: 'ArquitectaBold';
    src: url('/fonts/ArquitectaBold-webfont.eot');
    src: url('/fonts/ArquitectaBold-webfont.eot?#iefix') format('embedded-opentype'),
         url('/fonts/ArquitectaBold-webfont.woff') format('woff'),
         url('/fonts/ArquitectaBold-webfont.ttf') format('truetype'),
         url('/fonts/ArquitectaBold-webfont.svg#arquitecta_boldregular') format('svg');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrime';
    src: url('/fonts/courier_prime-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrimeItalic';
    src: url('/fonts/courier_prime_italic-webfont.woff') format('woff');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'BebasNeue';
    src: url('/fonts/bebasneue-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

// Add font styles to the document
const styleElement = document.createElement('style');
styleElement.textContent = fontStyle;
document.head.appendChild(styleElement);

const MENU_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Directors', href: '/directors' },
  { label: 'Contact', href: '/contact' },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [directors, setDirectors] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleVideoClick = useCallback((video) => {
    // Use the videos array directly since we're only interested in the current video
    setSelectedVideo(video);
    // We'll find the index when opening the modal to ensure we have the latest data
    const currentVideos = videos.filter(v => featured.some(f => f.video_id === v.id));
    const index = currentVideos.findIndex(v => v.id === video.id);
    setSelectedVideoIndex(Math.max(0, index));
    setIsModalOpen(true);
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';
  }, [featured, videos]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedVideo(null);
    // Re-enable body scroll when modal is closed
    document.body.style.overflow = 'unset';
  }, []);
  
  // Fetch directors for menu
  useEffect(() => {
    async function fetchDirectors() {
      const apiUrl = `${API_URL}/directors`;
      try {
        console.log('Fetching directors from:', apiUrl);
        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          mode: 'cors'
        });
        console.log('Directors response status:', res.status);
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to fetch directors: ${res.status} ${res.statusText}`);
        }
        
        const data = await res.json();
        console.log('Directors data received:', data);
        setDirectors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error in fetchDirectors:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        setDirectors([]);
      }
    }
    fetchDirectors();
  }, []);

  // Fetch featured videos and all videos
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [featuredSpots, allVideos] = await Promise.all([
          fetch(`${API_URL}/featured`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            mode: 'cors'
          }).then(res => res.json()),
          fetch(`${API_URL}/videos`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            mode: 'cors'
          }).then(res => res.json())
        ]);
        
        // Map featured spots to include full video data and director info
        const featuredWithDetails = featuredSpots.map(spot => {
          const video = allVideos.find(v => v.id === spot.video_id);
          return video ? { ...spot, ...video } : spot;
        });
        
        setFeatured(featuredWithDetails);
        setVideos(allVideos);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);
  
  // Helper to get video info from featured with director names
  const featuredVideos = featured.map(f => {
    const video = videos.find(v => v.id === f.video_id);
    if (!video) return null;
    return {
      ...video,
      director_name: directors.find(d => d.id === video.director_id)?.name || ''
    };
  }).filter(Boolean);

  // Menu state and refs
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuCloseTimeout = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);
  
  // Measure menu height when it opens or when content changes
  useEffect(() => {
    if (menuRef.current && menuOpen) {
      // Add 50px to account for the top padding and any potential gaps
      setMenuHeight(menuRef.current.offsetHeight + 50);
    }
  }, [menuOpen, directors]);

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuOpen && menuRef.current && menuButtonRef.current &&
          !menuRef.current.contains(event.target) &&
          !menuButtonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  // Menu hover handlers
  const handleMenuEnter = () => {
    clearTimeout(menuCloseTimeout.current);
    setIsMenuHovered(true);
    setMenuOpen(true);
  };

  const handleMenuLeave = () => {
    setIsMenuHovered(false);
    menuCloseTimeout.current = setTimeout(() => {
      if (!isHovering) setMenuOpen(false);
    }, 300);
  };

  const handleButtonEnter = () => {
    clearTimeout(menuCloseTimeout.current);
    setIsHovering(true);
    setMenuOpen(true);
  };

  const handleButtonLeave = () => {
    setIsHovering(false);
    menuCloseTimeout.current = setTimeout(() => {
      if (!isMenuHovered) setMenuOpen(false);
    }, 300);
  };

  return (
    <ParallaxProvider 
      smoothScroll={true} 
      smoothScrollDuration={800}
      scrollAxis="vertical"
    >
      <div style={{ minHeight: '100vh' }}>
        {/* Navigation Menu */}
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: menuOpen ? `translate(-50%, ${menuHeight}px)` : 'translate(-50%, 0)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            transition: 'transform 0.3s ease-in-out',
            transformOrigin: 'center top',
            willChange: 'transform',
            pointerEvents: 'none'
          }}
        >
          {/* Hamburger Menu Button */}
          <div 
            ref={menuButtonRef}
            onClick={() => setMenuOpen(!menuOpen)}
            onMouseEnter={handleButtonEnter}
            onMouseLeave={handleButtonLeave}
            style={{
              cursor: 'pointer',
              width: '24px',
              height: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '5px 0',
              boxSizing: 'content-box',
              pointerEvents: 'auto'
            }}
          >
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              opacity: menuOpen ? 0 : 1,
              transform: menuOpen ? 'translateX(20px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'
            }}></span>
          </div>

          {/* TDYK Logo */}
          <Link to="/" style={{
            display: 'block',
            maxWidth: '150px',
            height: 'auto',
            transition: 'transform 0.3s ease-in-out',
            pointerEvents: 'auto'
          }}>
            <img 
              src="/content/images/thedevilyouknow.tv/tdyk_logo.png" 
              alt="TDYK Logo" 
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                transition: 'opacity 0.3s ease',
                opacity: menuOpen ? 1 : 1
              }}
            />
          </Link>
        </div>

        {/* Menu Panel */}
        <div 
          ref={menuRef}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            backgroundColor: '#fff',
            zIndex: 998,
            padding: '40px 0 10px',
            opacity: menuOpen ? 1 : 0,
            pointerEvents: menuOpen ? 'auto' : 'none',
            transform: menuOpen ? 'translateY(0)' : 'translateY(-100%)',
            transition: 'all 0.3s ease-in-out',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            maxHeight: menuOpen ? 'none' : '0',
            paddingTop: menuOpen ? '40px' : '0',
            paddingBottom: menuOpen ? '10px' : '0',
            visibility: menuOpen ? 'visible' : 'hidden'
          }}
          onMouseEnter={handleMenuEnter}
          onMouseLeave={handleMenuLeave}
        >
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px'
          }}>
            {/* Directors Section */}
            <div style={{ 
              marginBottom: '15px',
              textAlign: 'center' 
            }}>
              <h3 style={{
                fontSize: '12px',
                fontWeight: 'normal',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                margin: '0 0 15px',
                color: '#666',
                fontFamily: 'BebasNeue, sans-serif'
              }}>
                Directors
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}>
                {directors.map(director => (
                  <Link
                    key={director.id}
                    to={`/directors/${director.id}`}
                    style={{
                      color: '#333',
                      textDecoration: 'none',
                      fontSize: '20px', // Reduced font size
                      margin: '0 8px', // Added horizontal spacing
                      fontWeight: 'normal',
                      fontFamily: 'BebasNeue, sans-serif',
                      lineHeight: '1.2',
                      transition: 'all 0.2s ease',
                      opacity: 0.8,
                      ':hover': {
                        opacity: 1,
                        color: '#000'
                      }
                    }}
                    onClick={() => setMenuOpen(false)}
                  >
                    {director.name}
                  </Link>
                ))}
              </div>
              <div style={{
                width: '100%',
                height: '1px',
                backgroundColor: '#eee',
                margin: '15px auto',
                maxWidth: '500px'
              }}></div>
            </div>

            {/* Main Menu Links */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px 20px',
              marginBottom: '10px',
              justifyContent: 'center',
              maxWidth: '800px',
              marginLeft: 'auto',
              marginRight: 'auto',
              padding: '0 20px'
            }}>
              {MENU_LINKS.filter(link => link.href !== '/directors').map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  style={{
                    color: 'rgba(0, 0, 0, 0.8)',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontFamily: 'BebasNeue, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    padding: '3px 0',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      color: '#000',
                      opacity: 1
                    }
                  }}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ width: '100%', background: '#000', position: 'relative', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ color: '#fff', textAlign: 'center', paddingTop: '120px', fontSize: '28px' }}>
              Loading...
            </div>
          ) : (
            <div style={{ minHeight: '100vh' }}>
              {featuredVideos.map((video, idx) => (
                <div 
                  key={video.id}
                  style={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    margin: 0,
                    padding: 0,
                    zIndex: 10 + idx,
                    overflow: 'hidden'
                  }}
                >
                  <Parallax 
                    speed={-20}
                    style={{
                      position: 'relative',
                      width: '100vw',
                      height: '100vh',
                      willChange: 'transform'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      backgroundImage: video.thumbnail && video.thumbnail.trim()
                        ? `url(${video.thumbnail})`
                        : `url(/content/images/thedevilyouknow.tv/tdyk_logo.png)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      zIndex: 1
                    }} />
                    <div style={{
                      position: 'relative',
                      zIndex: 2,
                      width: '100vw',
                      margin: 0,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100vh',
                      padding: 0
                    }}>
                      <Parallax speed={10}>
                        <div 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleVideoClick(video)}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.2rem',
                            textAlign: 'center',
                            textShadow: '-1px -1px 0 #000',
                            padding: '0.5rem'
                          }}>
                            {/* Title - Courier Prime Italic */}
                            <div style={{
                              fontFamily: 'CourierPrimeItalic, monospace',
                              fontSize: 'clamp(1.6rem, 3.25vw, 2.9rem)',
                              color: '#fff',
                              fontStyle: 'italic',
                              textShadow: '-1px -1px 0 #000',
                              margin: 0
                            }}>
                              {video.title || 'Untitled'}
                            </div>
                            
                            {/* Client - BebasNeue */}
                            {video.client && (
                              <div style={{
                                fontFamily: 'BebasNeue, sans-serif',
                                fontSize: 'clamp(3rem, 9vw, 7rem)',
                                lineHeight: 0.9,
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                textShadow: '-1px -1px 0 #000',
                                margin: 0
                              }}>
                                {video.client}
                              </div>
                            )}
                            
                            {/* Director - Courier Prime */}
                            {video.director_name && (
                              <div style={{
                                fontFamily: 'CourierPrime, monospace',
                                fontSize: 'clamp(0.9rem, 1.8vw, 1.6rem)',
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                textShadow: '-1px -1px 0 #000',
                                margin: 0
                              }}>
                                {video.director_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </Parallax>
                    </div>
                  </Parallax>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {isModalOpen && selectedVideo && (
        <VideoModal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          video={selectedVideo}
          videos={videos.filter(v => featured.some(f => f.video_id === v.id))}
          currentIndex={selectedVideoIndex}
          onVideoChange={(newIndex) => {
            const currentVideos = videos.filter(v => featured.some(f => f.video_id === v.id));
            if (currentVideos[newIndex]) {
              setSelectedVideoIndex(newIndex);
              setSelectedVideo(currentVideos[newIndex]);
            }
          }}
        />
      )}
    </ParallaxProvider>
  );
}
